import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const getRestaurantInfoSkill: Skill = {
  id: "getRestaurantInfo",
  description:
    "Obtiene información del restaurante (nombre, dirección, teléfono, país, logo).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Consultando datos del restaurante…",
      successMsg: "✓ Datos obtenidos",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}`,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: {
              id: data?.id,
              name: data?.name,
              description: data?.description,
              address: data?.address,
              phone: data?.phone,
              country: data?.country,
              logoUrl: data?.logoUrl,
              isActive: data?.isActive,
            },
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateRestaurantSkill: Skill = {
  id: "updateRestaurant",
  description:
    "Actualiza los datos básicos del restaurante (nombre, descripción, dirección, teléfono, país).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string" },
      description: { type: "string" },
      address: { type: "string" },
      phone: { type: "string" },
      country: { type: "string", description: "ISO-3166 alpha-2 (CO, US, MX, ES, ...)" },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nombre", value: patch.name as string }); }
    if (args.description !== undefined) { patch.description = String(args.description); details.push({ label: "Descripción", value: patch.description as string }); }
    if (args.address !== undefined) { patch.address = String(args.address); details.push({ label: "Dirección", value: patch.address as string }); }
    if (args.phone !== undefined) { patch.phone = String(args.phone); details.push({ label: "Teléfono", value: patch.phone as string }); }
    if (args.country) { patch.country = String(args.country).toUpperCase(); details.push({ label: "País", value: patch.country as string }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar restaurante",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando datos…",
      successMsg: "✓ Restaurante actualizado",
      restaurantId,
      targetTab: "info",
      targetTabLabel: "Información",
      run: async () => {
        try {
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}`,
            patch,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { id: data?.id ?? restaurantId, ...patch },
            humanSummary: "Datos del restaurante actualizados.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const getRestaurantPreferencesSkill: Skill = {
  id: "getRestaurantPreferences",
  description:
    "Obtiene las preferencias del restaurante (carta digital, diseño, paleta, configuración cocina).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Consultando preferencias…",
      successMsg: "✓ Preferencias obtenidas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/preferences`,
            ctx.accessToken,
          );
          return { ok: true as const, result: data };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateRestaurantPreferencesSkill: Skill = {
  id: "updateRestaurantPreferences",
  description:
    "Actualiza preferencias (carta digital on/off, diseño/paleta).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      menuEnabled: { type: "boolean" },
      menuDesign: { type: "string" },
      menuPalette: { type: "string" },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (typeof args.menuEnabled === "boolean") {
      patch.menuEnabled = args.menuEnabled;
      details.push({ label: "Carta digital", value: args.menuEnabled ? "Activa" : "Inactiva" });
    }
    if (args.menuDesign) { patch.menuDesign = String(args.menuDesign); details.push({ label: "Diseño", value: patch.menuDesign as string }); }
    if (args.menuPalette) { patch.menuPalette = String(args.menuPalette); details.push({ label: "Paleta", value: patch.menuPalette as string }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar preferencias",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando preferencias…",
      successMsg: "✓ Preferencias actualizadas",
      restaurantId,
      targetTab: "preferencias",
      targetTabLabel: "Preferencias",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/preferences`,
            patch,
            ctx.accessToken,
          );
          return { ok: true as const, result: patch, humanSummary: "Preferencias actualizadas." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const toggleRestaurantActiveSkill: Skill = {
  id: "toggleRestaurantActive",
  description:
    "Activa o desactiva el restaurante completo. DESACTIVAR detiene operaciones — usar con cuidado.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      activate: { type: "boolean" },
    },
    required: ["restaurantId", "activate"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    const activate = !!args.activate;

    const ok = await ctx.confirmation.confirm({
      title: activate ? "Activar restaurante" : "Desactivar restaurante",
      description: activate
        ? "Vas a re-activar el restaurante."
        : "Vas a DESACTIVAR el restaurante. Las operaciones se detendrán hasta que se reactive.",
      confirmLabel: activate ? "Activar" : "Desactivar",
      danger: !activate,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: activate ? "Activando restaurante…" : "Desactivando restaurante…",
      successMsg: activate ? "✓ Activado" : "✓ Desactivado",
      run: async () => {
        try {
          if (activate) {
            await apiWithAuth.patch(
              `/restaurants/${restaurantId}/activate`,
              {},
              ctx.accessToken,
            );
          } else {
            await apiWithAuth.delete(
              `/restaurants/${restaurantId}`,
              ctx.accessToken,
            );
          }
          return {
            ok: true as const,
            result: { isActive: activate },
            humanSummary: activate ? "Restaurante activado." : "Restaurante desactivado.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
